import { DocumentMetadata as BaseDocumentMetadata } from './documents';

export interface ComplianceRule {
  id: string;
  fiscalSponsorId: string;
  type: ComplianceRuleType;
  name: string;
  description: string;
  criteria: ComplianceCriteria;
  severity: 'low' | 'medium' | 'high';
  status: 'active' | 'inactive';
  actions: ComplianceAction[];
  metadata: Record<string, any>;
}

export type ComplianceRuleType = 'transaction_limit' | 'fund_restriction' | 'regulatory_requirement' | 'filing_deadline' | 'audit_requirement';

export interface ComplianceCriteria {
  conditions: Array<{
    field: string;
    operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains';
    value: any;
  }>;
  threshold?: number;
  timeframe?: {
    start?: Date;
    end?: Date;
    rolling?: number;
  };
}

export interface ComplianceAction {
  type: 'notify' | 'block' | 'require_approval' | 'create_task';
  config: Record<string, any>;
}

export interface ComplianceAlert {
  id: string;
  fiscalSponsorId: string;
  ruleId: string;
  entityType: 'transaction' | 'fund' | 'grant' | 'document';
  entityId: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  details: Record<string, any>;
  status: 'open' | 'acknowledged' | 'resolved';
  createdAt: Date;
  updatedAt: Date;
}

export interface FilingRequirement {
  id: string;
  fiscalSponsorId: string;
  type: 'form_990' | 'state_registration' | 'audit' | 'other';
  name: string;
  description: string;
  dueDate: Date;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected';
  assignedTo?: string;
  documents: Array<{
    id: string;
    type: string;
    status: 'required' | 'submitted' | 'approved';
  }>;
  metadata: Record<string, any>;
}

export interface RegulatoryRequirement {
  id: string;
  name: string;
  description: string;
  authority: string;
  deadline?: Date;
  status: 'active' | 'inactive';
}

export interface ComplianceCheck {
  ruleId: string;
  checkType: ComplianceRuleType;
  timestamp: Date;
  compliant: boolean;
  details: Record<string, any>;
  error?: string;
}

export enum ComplianceStatus {
  COMPLIANT = 'COMPLIANT',
  AT_RISK = 'AT_RISK',
  NON_COMPLIANT = 'NON_COMPLIANT',
  PENDING = 'PENDING'
}

export enum ComplianceType {
  FEDERAL = 'FEDERAL',
  STATE = 'STATE',
  LOCAL = 'LOCAL',
  INTERNAL = 'INTERNAL'
}

export interface ComplianceEvent {
  id: string;
  date: Date;
  type: string;
  description: string;
  documents?: BaseDocumentMetadata[];
  status: ComplianceStatus;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ComplianceRequirement {
  id: string;
  name: string;
  description: string;
  type: ComplianceType;
  jurisdiction: string;
  dueDate: Date;
  status: ComplianceStatus;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  documents: BaseDocumentMetadata[];
  events: ComplianceEvent[];
  assignedTo?: string;
  createdAt: Date;
  updatedAt: Date;
  nextReviewDate?: Date;
  recurringPeriod?: 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
}

export interface ComplianceThreshold {
  id: string;
  name: string;
  description: string;
  type: string;
  amount: number;
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ANNUALLY';
  status: 'ACTIVE' | 'INACTIVE';
  action: 'NOTIFY' | 'BLOCK' | 'REVIEW';
  conditions: {
    metric: string;
    operator: '>' | '<' | '==' | '>=' | '<=';
    value: number;
  }[];
  notifications: {
    type: 'EMAIL' | 'SYSTEM' | 'SMS';
    recipients: string[];
  }[];
  createdAt: Date;
  updatedAt: Date;
  lastTriggered?: Date;
}

export interface ComplianceReport {
  id: string;
  period: {
    start: Date;
    end: Date;
  };
  requirements: ComplianceRequirement[];
  thresholds: ComplianceThreshold[];
  status: ComplianceStatus;
  summary: {
    total: number;
    compliant: number;
    atRisk: number;
    nonCompliant: number;
    pending: number;
  };
  generatedAt: Date;
  generatedBy: string;
}

export interface ComplianceDocument {
  id: string;
  name: string;
  type: string;
  uploadDate: Date;
  expirationDate?: Date;
  status: 'VALID' | 'EXPIRED' | 'PENDING';
}

export enum DocumentType {
  COMPLIANCE_DOCUMENT = 'COMPLIANCE_DOCUMENT',
  TAX_DOCUMENT = 'TAX_DOCUMENT',
  LEGAL_DOCUMENT = 'LEGAL_DOCUMENT',
  FINANCIAL_DOCUMENT = 'FINANCIAL_DOCUMENT',
  OTHER = 'OTHER'
}

export interface DocumentMetadata {
  type: DocumentType;
  metadata: {  // Nested metadata object
    requirementId?: string;
    status?: string;
    [key: string]: any;
  };
}

export interface StateRequirement {
  id: string;
  state: string;
  requirement_type: string;
  description: string;
  due_date: Date;
  status: 'pending' | 'completed' | 'overdue';
  documents_required: string[];
  renewal_frequency: 'annual' | 'biennial' | 'triennial';
}

export interface FederalRequirement {
  id: string;
  requirement_type: string;
  description: string;
  due_date: Date;
  status: 'pending' | 'completed' | 'overdue';
  documents_required: string[];
  agency: string;
  form_number?: string;
} 